/**
 * CRM type definitions.
 * All types use camelCase (frontend convention). API responses are
 * transformed from snake_case in the useCRM hook.
 */

// ── Timeline ────────────────────────────────────────────────────────────────

export interface TimelineEvent {
  id: string
  eventType: string
  title: string
  description: string | null
  sourceType: string | null
  sourceId: string | null
  metadata: Record<string, unknown> | null
  icon: string | null
  color: string | null
  actorName: string | null
  actorType: string | null
  createdAt: string
}

export interface TimelineResponse {
  events: TimelineEvent[]
  hasMore: boolean
  nextCursor: string | null
}

// ── Contacts ────────────────────────────────────────────────────────────────

export interface CrmContactSummary {
  id: string
  channel: string
  channelUserId: string
  displayName: string | null
  isBlocked: boolean
  isDeleted: boolean
  tags: string[]

  // CRM metrics
  totalOrders: number
  totalSpentCents: number
  currency: string
  averageOrderCents: number
  lastOrderAt: string | null

  // Customer details
  email: string | null
  languagePreference: string | null
  loyaltyTier: string | null

  // Timestamps
  firstSeenAt: string | null
  lastSeenAt: string | null
  conversationCount: number
}

export interface CrmContactDetail {
  id: string
  channel: string
  channelUserId: string
  displayName: string | null
  userId: string | null
  isBlocked: boolean
  isVerified: boolean
  isDeleted: boolean
  deletedAt: string | null
  notes: string | null
  tags: string[]
  defaultPersonaId: string | null
  metadata: Record<string, unknown> | null

  // CRM metrics
  totalOrders: number
  totalSpentCents: number
  currency: string
  averageOrderCents: number
  lastOrderAt: string | null
  lastOrderId: string | null

  // Customer details
  email: string | null
  birthday: string | null
  languagePreference: string | null

  // AI insights
  aiInsights: AiInsights | null

  // Addresses
  addresses: CrmContactAddress[]

  // Loyalty
  loyaltyTier: string | null
  loyaltyPoints: number
  loyaltyEnrolledAt: string | null

  // Fiscal data
  fiscalData: Record<string, unknown> | null

  // Timestamps
  firstSeenAt: string | null
  lastSeenAt: string | null
  conversationCount: number
}

export interface AiInsights {
  events?: Array<{
    type: string
    date?: string
    person?: string
    notes?: string
    recurrence?: string
    source?: string
  }>
  preferences?: Record<string, string>
  dietary?: string[]
  declined?: string[]
  observations?: Array<{
    date?: string
    note: string
    source?: string
  }>
}

export interface CrmContactAddress {
  id: string
  label: string | null
  rawText: string
  formatted: string | null
  lat: number | null
  lng: number | null
  components: unknown[] | null
  isDefault: boolean
  source: 'order' | 'staff'
  sourceOrderId: string | null
  lastUsedAt: string | null
  createdAt: string | null
}

export interface CrmContactUpdate {
  displayName?: string | null
  notes?: string
  tags?: string[]
  isBlocked?: boolean
  email?: string | null
  birthday?: string | null
  languagePreference?: string | null
}

export interface CrmContactListResponse {
  contacts: CrmContactSummary[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export interface MergePreviewField {
  field: string
  primary: unknown
  secondary: unknown
  result: unknown
}

export interface MergePreview {
  primaryId: string
  secondaryId: string
  primaryName: string | null
  secondaryName: string | null
  fields: MergePreviewField[]
  aiInsights: MergePreviewField
  reassigned: Record<string, number>
  secondaryFate: string
}

// ── Pipeline ────────────────────────────────────────────────────────────────

export interface PipelineStage {
  id: string
  name: string
  order: number
  probability: number
  color: string
  isWon: boolean
  isLost: boolean
}

export interface Pipeline {
  id: string
  name: string
  description: string | null
  stages: PipelineStage[]
  isDefault: boolean
  isActive: boolean
  currency: string | null
  autoCreateFromOrders: boolean
  autoCreateStageId: string | null
  createdAt: string | null
  updatedAt: string | null
}

// ── Deal ────────────────────────────────────────────────────────────────────

export interface StageHistoryEntry {
  stageId: string
  stageName: string | null
  enteredAt: string
  exitedAt: string | null
}

export interface Deal {
  id: string
  pipelineId: string
  contactId: string | null
  companyId: string | null
  orderId: string | null
  name: string
  stageId: string
  amountCents: number | null
  currency: string | null
  probability: number | null
  expectedCloseDate: string | null
  actualCloseDate: string | null
  stageEnteredAt: string | null
  stageHistory: StageHistoryEntry[] | null
  source: string | null
  sourceDetail: string | null
  lossReason: string | null
  tags: string[] | null
  notes: string | null
  customFields: Record<string, unknown> | null
  assignedTo: string | null
  isDeleted: boolean
  createdAt: string | null
  updatedAt: string | null
  wonAt: string | null
  lostAt: string | null

  // Denormalized
  contactName: string | null
  pipelineName: string | null
  stageName: string | null
  stageColor: string | null
}

export interface DealCreate {
  name: string
  pipelineId: string
  stageId: string
  contactId?: string
  amountCents?: number
  currency?: string
  expectedCloseDate?: string
  source?: string
  notes?: string
  tags?: string[]
}

export interface DealListResponse {
  deals: Deal[]
  total: number
}

export interface PipelineStageStats {
  stageId: string
  stageName: string
  color: string
  count: number
  totalCents: number
  weightedCents: number
}

export interface PipelineStats {
  pipelineId: string
  pipelineName: string
  currency: string | null
  stages: PipelineStageStats[]
  totalDeals: number
  totalValueCents: number
  weightedValueCents: number
}

// ── Segment ─────────────────────────────────────────────────────────────────

export interface FilterCondition {
  field: string
  op: string
  value?: string | number | boolean | string[]
}

export interface FilterGroup {
  operator: 'and' | 'or'
  conditions: Array<FilterCondition | FilterGroup>
}

export interface Segment {
  id: string
  name: string
  description: string | null
  color: string | null
  icon: string | null
  filters: FilterGroup
  isSystem: boolean
  isPinned: boolean
  cachedCount: number
  cachedAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface SegmentCreate {
  name: string
  description?: string
  color?: string
  icon?: string
  filters: FilterGroup
  isPinned?: boolean
}

// Available filter fields for the segment builder UI
export const SEGMENT_FILTER_FIELDS = [
  { value: 'channel', label: 'Channel', type: 'select', options: ['whatsapp', 'web'] },
  { value: 'display_name', label: 'Name', type: 'text' },
  { value: 'email', label: 'Email', type: 'text' },
  { value: 'is_blocked', label: 'Is Blocked', type: 'boolean' },
  { value: 'language_preference', label: 'Language', type: 'text' },
  { value: 'loyalty_tier', label: 'Loyalty Tier', type: 'select', options: ['member', 'silver', 'gold'] },
  { value: 'total_orders', label: 'Total Orders', type: 'number' },
  { value: 'total_spent_cents', label: 'Total Spent (cents)', type: 'number' },
  { value: 'average_order_cents', label: 'Avg Order (cents)', type: 'number' },
  { value: 'tags', label: 'Tags', type: 'tag' },
  { value: 'first_seen_at', label: 'First Seen', type: 'date' },
  { value: 'last_seen_at', label: 'Last Active', type: 'date' },
  { value: 'last_order_at', label: 'Last Order', type: 'date' },
] as const

export const SEGMENT_OPERATORS: Record<string, Array<{ value: string; label: string }>> = {
  text: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'is_set', label: 'is set' },
    { value: 'is_not_set', label: 'is not set' },
  ],
  number: [
    { value: 'eq', label: 'equals' },
    { value: 'gt', label: 'greater than' },
    { value: 'gte', label: 'greater than or equal' },
    { value: 'lt', label: 'less than' },
    { value: 'lte', label: 'less than or equal' },
  ],
  select: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  boolean: [
    { value: 'eq', label: 'is' },
  ],
  date: [
    { value: 'days_ago_lt', label: 'within last (days)' },
    { value: 'days_ago_gt', label: 'more than (days ago)' },
    { value: 'is_set', label: 'is set' },
    { value: 'is_not_set', label: 'is not set' },
  ],
  tag: [
    { value: 'contains', label: 'includes' },
    { value: 'not_contains', label: 'does not include' },
  ],
}

// ── Company ─────────────────────────────────────────────────────────────────

export interface CompanyContactInfo {
  id: string
  displayName: string | null
  channelUserId: string
  channel: string
  jobTitle: string | null
  totalSpentCents: number
}

export interface Company {
  id: string
  name: string
  domain: string | null
  industry: string | null
  size: string | null
  website: string | null
  phone: string | null
  address: string | null
  city: string | null
  state: string | null
  country: string | null
  postalCode: string | null
  tags: string[] | null
  notes: string | null
  fiscalData: Record<string, unknown> | null
  customFields: Record<string, unknown> | null
  totalContacts: number
  totalRevenueCents: number
  currency: string | null
  isDeleted: boolean
  createdAt: string | null
  updatedAt: string | null
  contacts: CompanyContactInfo[] | null
}

export interface CompanyCreate {
  name: string
  domain?: string
  industry?: string
  phone?: string
  address?: string
  city?: string
  country?: string
  tags?: string[]
  notes?: string
}

export interface CompanyListResponse {
  companies: Company[]
  total: number
}

// ── AI CRM ──────────────────────────────────────────────────────────────────

export interface AiSummary {
  contactId: string
  summary: string
  generatedAt: string
  isCached: boolean
}

export interface SuggestedAction {
  id: string
  title: string
  description: string | null
  actionType: string
  priority: string
  reason: string
  contactId: string | null
  dealId: string | null
}

export interface ScoreComponent {
  name: string
  score: number
  weight: number
  description: string | null
}

export interface ContactScore {
  contactId: string
  totalScore: number
  components: ScoreComponent[]
  sentiment: string | null
  sentimentTrend: string | null
}

export interface DealScore {
  dealId: string
  totalScore: number
  components: ScoreComponent[]
}

// ── Activity ────────────────────────────────────────────────────────────────

export type ActivityType = 'task' | 'note' | 'call' | 'meeting'

export interface Activity {
  id: string
  activityType: ActivityType
  title: string | null
  description: string | null
  contactId: string | null
  dealId: string | null
  companyId: string | null
  isCompleted: boolean
  completedAt: string | null
  dueDate: string | null
  priority: string
  durationMinutes: number | null
  outcome: string | null
  channel: string | null
  direction: string | null
  sentiment: string | null
  aiSummary: string | null
  assignedTo: string | null
  createdBy: string | null
  createdAt: string | null
  updatedAt: string | null
  contactName: string | null
  dealName: string | null
}

export interface ActivityCreate {
  activityType: ActivityType
  title?: string
  description?: string
  contactId?: string
  dealId?: string
  dueDate?: string
  priority?: string
}

// ── Automation ──────────────────────────────────────────────────────────────

export interface AutomationTrigger {
  type: string
  conditions?: Record<string, unknown>
}

export interface AutomationAction {
  type: string
  config: Record<string, unknown>
}

export interface Automation {
  id: string
  name: string
  description: string | null
  isActive: boolean
  trigger: AutomationTrigger
  actions: AutomationAction[]
  runCount: number
  lastRunAt: string | null
  lastError: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface AutomationTemplate {
  id: string
  name: string
  description: string
  trigger: AutomationTrigger
  actions: AutomationAction[]
  category: string
}

export const TRIGGER_TYPES = [
  { value: 'contact_created', label: 'Contact created' },
  { value: 'contact_tag_added', label: 'Tag added to contact' },
  { value: 'deal_created', label: 'Deal created' },
  { value: 'deal_stage_changed', label: 'Deal stage changed' },
  { value: 'deal_won', label: 'Deal won' },
  { value: 'deal_lost', label: 'Deal lost' },
  { value: 'order_status_changed', label: 'Order status changed' },
] as const

export const ACTION_TYPES = [
  { value: 'create_activity', label: 'Create task/activity' },
  { value: 'add_tag', label: 'Add tag' },
  { value: 'remove_tag', label: 'Remove tag' },
] as const

// ── Contact filters ─────────────────────────────────────────────────────────

export interface ContactFilters {
  search: string
  channel: string | null
  tag: string | null
  blocked: boolean | null
  includeDeleted: boolean
  sortBy: string
  sortOrder: 'asc' | 'desc'
  page: number
  perPage: number
}

export const DEFAULT_CONTACT_FILTERS: ContactFilters = {
  search: '',
  channel: null,
  tag: null,
  blocked: null,
  includeDeleted: false,
  sortBy: 'last_seen',
  sortOrder: 'desc',
  page: 1,
  perPage: 50,
}
